import { useState, useMemo, useEffect } from "react";
import "./day-plan-styles/DayPlanParent.css";
import StatusColumn from "./day-plan-components/StatusColumn";
import Modal from "../modal-component/Modal";
import TaskCompletionPrompt from "./day-plan-components/day-plan-modals/TaskCompletionPrompt";
import WarningPrompt from "./day-plan-components/day-plan-modals/WarningPrompt";
import ProductionBreakdown from "./day-plan-components/day-plan-modals/ProductionBreakdown";
import { useDataContext } from "../../data-context/DataContextProvider";
import { useTranslation } from "@preppa/translation";
import { PlanData, DailyTaskCard, TASK_STATUS } from "../../types/types";
import {
  getStatusFromCode,
  canTransition,
  getTransitionModalType,
  TransitionModalType,
  getTotalProducedVolume,
  getStatusCode,
  getDisplayConversion,
  hasProducedInput,
  hasAvailableInput,
} from "../../utils/planDataHelpers";
import { getPlanDayForWorksheet } from "../../utils/dateUtils";
import { buildTaskRange } from "../../utils/taskRange";
import { updatePlanData } from "../../api/apiCallsPlanData";

type PendingTransition = {
  task: PlanData;
  dailyCard?: DailyTaskCard;
  from: string;
  to: string;
};

function DailyPlan() {
  const {
    selectedDayView,
    planData,
    selectedWorksheet,
    selectedDate,
    buCode,
    buType,
    dispatch,
  } = useDataContext();
  const { translate } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<TransitionModalType>("none");
  const [pendingTransition, setPendingTransition] =
    useState<PendingTransition | null>(null);

  const [expandedTodoKey, setExpandedTodoKey] = useState<string | null>(null);
  const [expandedInProgressKey, setExpandedInProgressKey] = useState<
    string | null
  >(null);
  const [expandedDoneKey, setExpandedDoneKey] = useState<string | null>(null);

  const [isBreakdownOpen, setIsBreakdownOpen] = useState(false);
  const [breakdownPlanData, setBreakdownPlanData] = useState<PlanData | null>(null);

  const [dailyCardStatuses, setDailyCardStatuses] = useState<
    Map<string, string>
  >(new Map());

  const allDailyCards = useMemo(() => {
    if (!selectedWorksheet) return [];

    const shouldIncludeByPlanRules = (
      plannedVol: number | null,
      forecastVol: number | null,
    ): boolean => {
      if (plannedVol === null || plannedVol === undefined) {
        return (forecastVol ?? 0) > 0;
      }
      return plannedVol > 0;
    };

    const selectedPlanDay = getPlanDayForWorksheet(
      selectedDate,
      selectedWorksheet.prepDayBefore,
    );
    const dayPlanData = planData.find((day) => day.planDay === selectedPlanDay);

    if (!dayPlanData) return [];

    const cards: DailyTaskCard[] = [];

    dayPlanData.dayPlanDatas.forEach((item) => {
      if (
        item.worksheetData &&
        item.worksheetData.id === selectedWorksheet.id
      ) {
        if (item.itemSalesPlanDtos.length > 0) {
          const shouldIncludeCard = item.itemSalesPlanDtos.some((dto) =>
            shouldIncludeByPlanRules(dto.plannedVol, dto.forecastVol),
          );
          if (!shouldIncludeCard) {
            return;
          }

          const totalPlannedVol = item.itemSalesPlanDtos.reduce(
            (sum, dto) => sum + (dto.plannedVol || 0),
            0,
          );

          const totalForecastedVol = item.itemSalesPlanDtos.reduce(
            (sum, dto) => sum + (dto.forecastVol || 0),
            0,
          );

          // One task for the whole day, anchored on the item's last plan hour.
          const range = buildTaskRange(item.itemSalesPlanDtos);
          if (!range) return;

          cards.push({
            itemSalesPlan: range.anchorDto,
            parentPlanData: item,
            totalPlannedVol: Math.round(totalPlannedVol * 10) / 10,
            totalForecastedVol: Math.round(totalForecastedVol * 10) / 10,
            fromHour: range.fromHour,
            toHour: range.toHour,
          });
        }
      }
    });

    return cards;
  }, [planData, selectedWorksheet, selectedDate]);

  useEffect(() => {
    if (!selectedWorksheet) {
      setDailyCardStatuses(new Map());
      return;
    }

    const initialStatuses = new Map<string, string>();
    allDailyCards.forEach((card) => {
      initialStatuses.set(
        card.parentPlanData.itemCode,
        getStatusFromCode(card.itemSalesPlan.statusCode || "10"),
      );
    });
    setDailyCardStatuses(initialStatuses);
  }, [allDailyCards, selectedWorksheet]);

  const dailyTodoCards = useMemo(() => {
    return allDailyCards.filter((card) => {
      return (
        dailyCardStatuses.get(card.parentPlanData.itemCode) === TASK_STATUS.TODO
      );
    });
  }, [allDailyCards, dailyCardStatuses]);

  const dailyInProgressCards = useMemo(() => {
    return allDailyCards.filter((card) => {
      return (
        dailyCardStatuses.get(card.parentPlanData.itemCode) ===
        TASK_STATUS.IN_PROGRESS
      );
    });
  }, [allDailyCards, dailyCardStatuses]);

  const dailyDoneCards = useMemo(() => {
    return allDailyCards.filter((card) => {
      return (
        dailyCardStatuses.get(card.parentPlanData.itemCode) === TASK_STATUS.DONE
      );
    });
  }, [allDailyCards, dailyCardStatuses]);

  function updateDailyTaskStatus(
    card: DailyTaskCard,
    from: string,
    to: string,
  ): string {
    const key = card.parentPlanData.itemCode;
    const previousStatus = dailyCardStatuses.get(key) || from;

    const collapse = (current: string | null) => (current === key ? null : current);
    setExpandedTodoKey(collapse);
    setExpandedInProgressKey(collapse);
    setExpandedDoneKey(collapse);

    setDailyCardStatuses((prev) => {
      const newStatuses = new Map(prev);
      newStatuses.set(key, to);
      return newStatuses;
    });

    return previousStatus;
  }

  function revertDailyTaskStatus(
    card: DailyTaskCard,
    previousStatus: string,
  ): void {
    const key = card.parentPlanData.itemCode;

    setDailyCardStatuses((prev) => {
      const newStatuses = new Map(prev);
      newStatuses.set(key, previousStatus);
      return newStatuses;
    });
  }

  const callDailyStatusUpdateAPI = async (
    card: DailyTaskCard,
    newStatus: string,
    producedVol?: number,
    availableVol?: number,
  ): Promise<boolean> => {
    const statusCode = getStatusCode(newStatus);

    const finalProducedVol =
      producedVol !== undefined
        ? producedVol
        : card.itemSalesPlan.producedVol || 0;
    const finalAvailableVol =
      availableVol !== undefined
        ? availableVol
        : card.itemSalesPlan.availableVol || 0;
    const comment = card.itemSalesPlan.comment || "";

    return await updatePlanData({
      itemCode: card.parentPlanData.itemCode,
      fromHour: card.fromHour,
      toHour: card.toHour,
      buCode,
      buType,
      statusCode,
      producedVol: finalProducedVol,
      availableVol: finalAvailableVol,
      comment,
      callBack: dispatch,
    });
  };

  async function onDailyStatusChange(
    card: DailyTaskCard,
    from: string,
    to: string,
  ) {
    if (!canTransition(from, to)) {
      console.warn(`Transition from ${from} to ${to} is not allowed`);
      return;
    }

    const task = card.parentPlanData;
    const producedVol = hasProducedInput(task) ? (card.itemSalesPlan.producedVol || 0) : 0;
    const availableVol = hasAvailableInput(task) ? (card.itemSalesPlan.availableVol || 0) : 0;
    const plannedVol = card.totalPlannedVol || card.totalForecastedVol;

    const modalTypeNeeded = getTransitionModalType(
      from,
      to,
      producedVol,
      availableVol,
      plannedVol,
    );

    if (modalTypeNeeded === "blocked") {
      return;
    }

    const effectiveModalType = (modalTypeNeeded === "produceQuantity" && !hasProducedInput(task))
      ? "none"
      : modalTypeNeeded;

    if (effectiveModalType === "none") {
      const previousStatus = updateDailyTaskStatus(card, from, to);
      const success = await callDailyStatusUpdateAPI(card, to);
      if (!success) {
        revertDailyTaskStatus(card, previousStatus);
      }
    } else {
      setPendingTransition({ task, dailyCard: card, from, to });
      setModalType(effectiveModalType);
      setIsModalOpen(true);
    }
  }

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setPendingTransition(null);
    setModalType("none");
  };

  const handleProduceQtyConfirm = async (producedQty: number) => {
    if (!pendingTransition) return;

    const { dailyCard, from, to } = pendingTransition;

    handleModalCancel();

    if (dailyCard) {
      const previousStatus = updateDailyTaskStatus(dailyCard, from, to);
      const success = await callDailyStatusUpdateAPI(
        dailyCard,
        to,
        producedQty,
      );
      if (!success) {
        revertDailyTaskStatus(dailyCard, previousStatus);
      }
    }
  };

  const handleWarningConfirm = async () => {
    if (!pendingTransition) return;

    const { dailyCard, from, to } = pendingTransition;

    handleModalCancel();

    if (dailyCard) {
      const previousStatus = updateDailyTaskStatus(dailyCard, from, to);
      const success = await callDailyStatusUpdateAPI(
        dailyCard,
        to,
        0,
        hasAvailableInput(dailyCard.parentPlanData) ? (dailyCard.itemSalesPlan.availableVol || 0) : 0,
      );
      if (!success) {
        revertDailyTaskStatus(dailyCard, previousStatus);
      }
    }
  };

  const handleExpansionToggleTodo = (key: string) => {
    setExpandedTodoKey(expandedTodoKey === key ? null : key);
  };

  const handleExpansionToggleInProgress = (key: string) => {
    setExpandedInProgressKey(expandedInProgressKey === key ? null : key);
  };

  const handleExpansionToggleDone = (key: string) => {
    setExpandedDoneKey(expandedDoneKey === key ? null : key);
  };

  const handleViewBreakdown = (planData: PlanData) => {
    setBreakdownPlanData(planData);
    setIsBreakdownOpen(true);
  };

  const handleBreakdownClose = () => {
    setIsBreakdownOpen(false);
    setBreakdownPlanData(null);
  };

  return (
    <div id="day-plan-container">
      <div className="task-status-wrapper">
        <StatusColumn
          status={TASK_STATUS.TODO}
          label={translate("kitchenClient.dayPlan.statusColumns.todo")}
          dailyCards={dailyTodoCards}
          timeSegments={[]}
          expandedKey={expandedTodoKey}
          onToggleExpand={handleExpansionToggleTodo}
          onDailyStatusChange={onDailyStatusChange}
          onViewBreakdown={handleViewBreakdown}
        />
        <StatusColumn
          status={TASK_STATUS.IN_PROGRESS}
          label={translate("kitchenClient.dayPlan.statusColumns.inProgress")}
          dailyCards={dailyInProgressCards}
          timeSegments={[]}
          expandedKey={expandedInProgressKey}
          onToggleExpand={handleExpansionToggleInProgress}
          onDailyStatusChange={onDailyStatusChange}
          onViewBreakdown={handleViewBreakdown}
        />
        <StatusColumn
          status={TASK_STATUS.DONE}
          label={translate("kitchenClient.dayPlan.statusColumns.done")}
          dailyCards={dailyDoneCards}
          timeSegments={[]}
          expandedKey={expandedDoneKey}
          onToggleExpand={handleExpansionToggleDone}
          onDailyStatusChange={onDailyStatusChange}
          onViewBreakdown={handleViewBreakdown}
        />
      </div>
      <Modal isModalOpen={isModalOpen} onClose={handleModalCancel}>
        {modalType === "produceQuantity" && pendingTransition && (() => {
          const pd = pendingTransition.dailyCard?.parentPlanData ?? pendingTransition.task;
          const dc = getDisplayConversion(pd);
          const cf = dc?.conversionFactor ?? 1;
          const uom = dc ? dc.displayUom : (
            pendingTransition.dailyCard
              ? pendingTransition.dailyCard.itemSalesPlan.uomCode || ""
              : pendingTransition.task.itemSalesPlanDtos[0]?.uomCode || ""
          );
          return (
          <TaskCompletionPrompt
            initialProducedQty={
              pendingTransition.dailyCard
                ? pendingTransition.dailyCard.itemSalesPlan.producedVol || 0
                : getTotalProducedVolume(pendingTransition.task)
            }
            uomCode={uom}
            conversionFactor={cf}
            originStatus={pendingTransition.from}
            totalPlannedVol={
              pendingTransition.dailyCard &&
              pendingTransition.dailyCard.totalPlannedVol > 0
                ? pendingTransition.dailyCard.totalPlannedVol
                : pendingTransition.dailyCard?.totalForecastedVol || 0
            }
            availableVol={
              pendingTransition.dailyCard
                ? (hasAvailableInput(pendingTransition.dailyCard.parentPlanData) ? (pendingTransition.dailyCard.itemSalesPlan?.availableVol || 0) : 0)
                : 0
            }
            onConfirm={handleProduceQtyConfirm}
            onCancel={handleModalCancel}
          />
          );
        })()}
        {modalType === "warning" && pendingTransition && (
          <WarningPrompt
            message={translate("kitchenClient.dayPlan.prompts.warningMoveBackToTodo")}
            onConfirm={handleWarningConfirm}
            onCancel={handleModalCancel}
          />
        )}
      </Modal>
      <Modal isModalOpen={isBreakdownOpen} onClose={handleBreakdownClose}>
        {breakdownPlanData && (
          <ProductionBreakdown
            planData={breakdownPlanData}
            onClose={handleBreakdownClose}
          />
        )}
      </Modal>
    </div>
  );
}

export default DailyPlan;
